import {
  exportStudioBreakdownCsv,
  exportStudioBreakdownExcel,
  sumValues,
  toNum,
  type IFixedBreakdownRow,
  type IStudioBreakdownBlock,
  type IStudioBreakdownExcelConfig,
} from '@sg/utils/studioBreakdownExcel';

type THeadcountMetricKey =
  | 'management'
  | 'technology_and_research'
  | 'product_management_and_business_operations_and_development'
  | 'design'
  | 'sales_and_marketing'
  | 'customer_service'
  | 'functional_support'
  | 'intern'
  | 'contractor'
  | 'others'
  | 'l2_liveops';

/** forecast 行直接下发的汇总字段，与图表 LEVEL_2_SERIES_FORECAST 口径一致 */
type THeadcountForecastMetricKey =
  | 'l2_management'
  | 'l2_r_and_d'
  | 'l2_s_and_m'
  | 'l2_functional_support'
  | 'l2_liveops'
  | 'fte'
  | 'temporary';

export interface IHeadcountMonthRow {
  date: string;
  source?: 'actual' | 'forecast' | null;
  management?: number | null;
  technology_and_research?: number | null;
  product_management_and_business_operations_and_development?: number | null;
  design?: number | null;
  sales_and_marketing?: number | null;
  customer_service?: number | null;
  functional_support?: number | null;
  intern?: number | null;
  contractor?: number | null;
  others?: number | null;
  /** forecast 行的 Level 2 汇总字段（后端直接下发） */
  l2_management?: number | null;
  l2_r_and_d?: number | null;
  l2_s_and_m?: number | null;
  l2_functional_support?: number | null;
  l2_liveops?: number | null;
  /** forecast 行的 Level 1 汇总字段（后端直接下发） */
  fte?: number | null;
  temporary?: number | null;
}

/** Portfolio Overview download: one studio with nested month series. */
export interface IHeadcountPortfolioStudioRow {
  studio_id?: string;
  studio_name: string;
  series?: IHeadcountMonthRow[];
}

export interface IExportHeadcountExcelParams {
  datatime: string;
  name?: string;
  fileFormat?: 'excel' | 'csv';
  /** Studios page: flat month rows for one studio. */
  rawData?: IHeadcountMonthRow[];
  studioName?: string;
  /** Portfolio Overview: nested studios → series months. */
  studios?: IHeadcountPortfolioStudioRow[];
}

interface IHeadcountFixedRowDef {
  level1: string;
  level2: string;
  functions: string;
  sourceKey: THeadcountMetricKey | null;
  /** source === 'forecast' 时直接取该汇总字段的值，替代 sourceKey / calcFn */
  forecastKey?: THeadcountForecastMetricKey;
  calcFn?: (monthRow: IHeadcountMonthRow, totals: IRowTotals) => number | null;
}

interface IRowTotals {
  totalRandD: number | null;
  totalSandM: number | null;
}

const getRowTotals = (row: IHeadcountMonthRow): IRowTotals => ({
  // Subtotals mirror the Excel template's R&D and S&M groupings, not raw API keys.
  totalRandD: sumValues(
    row.technology_and_research,
    row.product_management_and_business_operations_and_development,
    row.design,
  ),
  totalSandM: sumValues(row.sales_and_marketing, row.customer_service),
});

const getRowValue = (fixedRow: IHeadcountFixedRowDef, row: IHeadcountMonthRow): number | null => {
  // forecast 行优先取后端下发的汇总字段，不取明细字段集合值
  if (row.source === 'forecast' && fixedRow.forecastKey) {
    return toNum(row[fixedRow.forecastKey]);
  }
  if (fixedRow.sourceKey) {
    return toNum(row[fixedRow.sourceKey]);
  }
  return fixedRow.calcFn ? fixedRow.calcFn(row, getRowTotals(row)) : null;
};

const HEADCOUNT_FIXED_ROW_DEFS: IHeadcountFixedRowDef[] = [
  {
    level1: 'FTE',
    level2: '',
    functions: 'Management',
    sourceKey: 'management',
    forecastKey: 'l2_management',
  },
  {
    level1: 'FTE',
    level2: 'R&D',
    functions: 'Technology and Research',
    sourceKey: 'technology_and_research',
  },
  {
    level1: 'FTE',
    level2: 'R&D',
    functions: 'Product Management and Business Operations and Development',
    sourceKey: 'product_management_and_business_operations_and_development',
  },
  {
    level1: 'FTE',
    level2: 'R&D',
    functions: 'Design',
    sourceKey: 'design',
  },
  {
    level1: 'FTE',
    level2: 'R&D',
    functions: 'Total R&D HC',
    sourceKey: null,
    forecastKey: 'l2_r_and_d',
    calcFn: (row) =>
      sumValues(
        row.technology_and_research,
        row.product_management_and_business_operations_and_development,
        row.design,
      ),
  },
  {
    level1: 'FTE',
    level2: 'S&M',
    functions: 'Sales and Marketing',
    sourceKey: 'sales_and_marketing',
  },
  {
    level1: 'FTE',
    level2: 'S&M',
    functions: 'Customer Service',
    sourceKey: 'customer_service',
  },
  {
    level1: 'FTE',
    level2: 'S&M',
    functions: 'Total S&M HC',
    sourceKey: null,
    forecastKey: 'l2_s_and_m',
    calcFn: (row) => sumValues(row.sales_and_marketing, row.customer_service),
  },
  {
    level1: 'FTE',
    level2: '',
    functions: 'Functional Support',
    sourceKey: 'functional_support',
    forecastKey: 'l2_functional_support',
  },
  {
    level1: 'FTE',
    level2: '',
    functions: 'Total FTE HC',
    sourceKey: null,
    forecastKey: 'fte',
    calcFn: (row, totals) => sumValues(row.management, totals.totalRandD, totals.totalSandM, row.functional_support),
  },
  {
    level1: 'Temporary',
    level2: '',
    functions: 'Intern',
    sourceKey: 'intern',
  },
  {
    level1: 'Temporary',
    level2: '',
    functions: 'Contractor',
    sourceKey: 'contractor',
  },
  {
    level1: 'Temporary',
    level2: '',
    functions: 'Others',
    sourceKey: 'others',
  },
  {
    level1: 'Temporary',
    level2: '',
    functions: 'Total Temporary HC',
    sourceKey: null,
    forecastKey: 'temporary',
    calcFn: (row) => sumValues(row.intern, row.contractor, row.others),
  },
];

/** LiveOps 行：数据中存在 l2_liveops 字段时，插入到 Total S&M HC 行之后 */
const LIVEOPS_ROW_DEF: IHeadcountFixedRowDef = {
  level1: 'LiveOps',
  level2: '',
  functions: 'Management',
  sourceKey: 'l2_liveops',
  forecastKey: 'l2_liveops',
};

const hasLiveopsField = (row: IHeadcountMonthRow): boolean => row.l2_liveops !== null && row.l2_liveops !== undefined;

const buildHeadcountFixedRows = (hasLiveops?: boolean): IFixedBreakdownRow<IHeadcountMonthRow>[] => {
  let rowDefs = HEADCOUNT_FIXED_ROW_DEFS;

  // 当数据中存在 l2_liveops 字段时，在 Total S&M HC 行后插入 LiveOps 行
  if (hasLiveops) {
    const totalSandMIndex = rowDefs.findIndex((rowDef) => rowDef.functions === 'Total S&M HC');
    if (totalSandMIndex !== -1) {
      // 拷贝后再插入，避免直接修改 HEADCOUNT_FIXED_ROW_DEFS 常量
      rowDefs = [...rowDefs];
      rowDefs.splice(totalSandMIndex + 1, 0, LIVEOPS_ROW_DEF);
    }
  }

  return rowDefs.map((rowDef) => ({
    level1: rowDef.level1,
    level2: rowDef.level2,
    level3: rowDef.functions,
    getValue: (monthRow) => getRowValue(rowDef, monthRow),
  }));
};

const buildStudioBlocks = (params: IExportHeadcountExcelParams): IStudioBreakdownBlock<IHeadcountMonthRow>[] => {

  // Portfolio Overview: nested studios[].series[]; shared HC template per studio.
  if (params.studios?.length) {
    return params.studios.map((studio) => {

      // 任一月份数据存在 l2_liveops 字段时，固定行模板中插入 LiveOps 行
      const hasLiveops = (studio.series ?? []).some(hasLiveopsField);

      const fixedRows = buildHeadcountFixedRows(hasLiveops);
      return {
        studioName: studio.studio_name || '',
        rawData: studio.series ?? [],
        fixedRows,
      };
    });
  }

  const fixedRows = buildHeadcountFixedRows();

  return [
    {
      studioName: params.studioName ?? '',
      rawData: params.rawData ?? [],
      fixedRows,
    },
  ];
};

const HC_EXPORT_BASE_NAME = 'Studio HC Tracking';

const buildHeadcountBreakdownConfig = (
  params: IExportHeadcountExcelParams,
): IStudioBreakdownExcelConfig<IHeadcountMonthRow> => {
  // File name: "Studio HC Tracking" or "Studio HC Tracking - Miniclip" on Studios page.
  const name =
    params.name ?? (params.studioName ? `${HC_EXPORT_BASE_NAME} - ${params.studioName}` : HC_EXPORT_BASE_NAME);

  return {
    name,
    sheetName: 'Studio HC',
    noteRows: [
      {
        text: 'Actual: Monthly Updates; Forecast: Quarterly Updates',
        bold: true,
        trailingYears: true,
      },
    ],
    infoHeaders: ['Studios', 'Level 1 Category', 'Level 2 Category', 'Functions'],
    datatime: params.datatime,
    getMonthKey: (row) => row.date,
    isActualMonth: (row) => {
      if (row.source === undefined || row.source === null) return null;
      return row.source === 'actual';
    },
    studioBlocks: buildStudioBlocks(params),
    // Functions (D) needs the wide column for long labels.
    infoWidths: [20, 16, 16, 52],
    // Functions-column totals: bold via cellHighlights (config-driven).
    cellHighlights: [
      {
        column: 4,
        texts: ['Total R&D HC', 'Total S&M HC', 'Total FTE HC', 'Total Temporary HC'],
        bold: true,
      },
    ],
  };
};

export const exportHeadcountExcel = async (params: IExportHeadcountExcelParams): Promise<void> => {
  const config = buildHeadcountBreakdownConfig(params);
  if (params.fileFormat === 'csv') {
    exportStudioBreakdownCsv(config);
    return;
  }
  await exportStudioBreakdownExcel(config);
};
